/**
 * Essential exports for RAG system
 * This provides fallback implementations of the key functions needed by the application
 */

// QueryType enum
export const QueryType = {
  GENERAL: 'general',
  TECHNIQUE: 'technique',
  PAYLOAD: 'payload',
  TOOL: 'tool',
  WAF_BYPASS: 'waf_bypass'
};

// RAG store
const store = {
  initialized: false,
  knowledgeUnits: [],
  apiKey: null,
  loadTimestamp: null,
  loadError: null
};

// getRAGStore function
export function getRAGStore() {
  return store;
}

// getAPIKey function
export function getAPIKey() {
  return store.apiKey;
}

// Enhanced searchKnowledgeUnits function with better matching
export function searchKnowledgeUnits(query) {
  if (!store.initialized || store.knowledgeUnits.length === 0) {
    console.warn('RAG store not initialized for search');
    return [];
  }
  
  const lowercaseQuery = query.toLowerCase().trim();
  
  // Extract main topic from query
  let mainTopic = lowercaseQuery;
  
  // Remove common prefixes that don't help with topic identification
  mainTopic = mainTopic.replace(/^(how to|what is|explain|craft|create|give me|show me|help with|i need|please provide)/i, '').trim();
  
  // Break query into meaningful keywords (exclude common words)
  const stopWords = ['the', 'a', 'an', 'and', 'or', 'but', 'is', 'are', 'for', 'to', 'in', 'with', 'by', 'at', 'from', 'which', 'that'];
  const keywords = mainTopic.split(/\s+/)
    .filter(word => word.length > 2)
    .filter(word => !stopWords.includes(word.toLowerCase()));
  
  console.log('Main topic:', mainTopic);
  console.log('Keywords:', keywords);
  
  // Helper function to safely check if a string includes a substring
  const safeIncludes = (text, search) => {
    if (typeof text !== 'string') return false;
    return text.toLowerCase().includes(search);
  };
  
  // Helper function to safely convert to lowercase
  const safeLowerCase = (text) => {
    if (typeof text !== 'string') return '';
    return text.toLowerCase();
  };
  
  // Direct lookup for specific common queries
  const directQueries = {
    'port scan': ['port scanning', 'network scanning', 'nmap', 'open ports'],
    'port scanner': ['port scanning', 'network scanning', 'nmap', 'open ports'],
    'scan for open ports': ['port scanning', 'network scanning', 'nmap', 'open ports'],
    'scan ports': ['port scanning', 'network scanning', 'nmap', 'open ports'],
    'portscan': ['port scanning', 'network scanning', 'nmap', 'open ports'],
    'network scan': ['port scanning', 'network scanning', 'nmap', 'open ports'],
    // Add more mappings for common queries that need specific responses
    'sql injection': ['sql injection', 'sqli'],
    'xss': ['cross-site scripting', 'xss'],
    'csrf': ['cross-site request forgery', 'csrf'],
    'ssrf': ['server side request forgery', 'ssrf'],
    // Add tool-related queries
    'tools for sql injection': ['sql injection tools', 'sqlmap'],
    'tools for scanning': ['network scanning tools', 'scanning tools', 'port scanner']
  };
  
  // Check if query exactly matches or contains one of our direct query mappings
  let matchedKeywords = [];
  for (const [directQuery, mappedTerms] of Object.entries(directQueries)) {
    if (lowercaseQuery.includes(directQuery)) {
      matchedKeywords = [...matchedKeywords, ...mappedTerms];
      // If exact match, prioritize even higher
      if (lowercaseQuery === directQuery) {
        console.log(`Direct match found for query: ${directQuery}`);
        // Look for knowledge units that exactly match these terms
        const exactMatches = store.knowledgeUnits.filter(unit => {
          const unitTitle = safeLowerCase(unit.title);
          const unitTags = unit.tags ? unit.tags.map(t => safeLowerCase(t)) : [];
          
          return mappedTerms.some(term => 
            unitTitle.includes(term) || 
            unitTags.some(tag => tag.includes(term))
          );
        });
        
        if (exactMatches.length > 0) {
          console.log(`Found ${exactMatches.length} exact matches for direct query: ${directQuery}`);
          return exactMatches;
        }
      }
    }
  }
  
  // Check query for tool-specific language
  const isToolQuery = lowercaseQuery.includes('tool') || 
                     lowercaseQuery.includes('scanner') || 
                     lowercaseQuery.includes('utility') ||
                     lowercaseQuery.includes('program') ||
                     lowercaseQuery.includes('software') ||
                     lowercaseQuery.includes('scan with');
  
  // Score each knowledge unit based on strict keyword matches
  const scoredUnits = store.knowledgeUnits.map(unit => {
    let score = 0;
    const unitTitle = safeLowerCase(unit.title);
    const unitDesc = safeLowerCase(unit.description || '');
    const unitTags = unit.tags ? unit.tags.map(t => safeLowerCase(t)) : [];
    
    // Special handling for tool queries
    if (isToolQuery && unit.tools && unit.tools.length > 0) {
      // For tool queries, prioritize units that have tools list
      score += 50;
      
      // If the query is about a specific type of tool, match against that type
      for (const keyword of keywords) {
        // Check if any tool's name or description matches the keyword
        const toolMatches = unit.tools.some(tool => 
          safeLowerCase(tool.name).includes(keyword) || 
          (tool.description && safeLowerCase(tool.description).includes(keyword))
        );
        
        if (toolMatches) {
          score += 50; // Strong boost for matching tools
        }
      }
    }
    
    // Check if the title contains ALL keywords (very strong signal)
    const allKeywordsInTitle = keywords.every(kw => unitTitle.includes(kw));
    if (allKeywordsInTitle) {
      score += 100;
    }
    
    // Check exact title match (highest priority)
    if (unitTitle === mainTopic) {
      score += 150;
    }
    
    // Check for main topic in title (high priority)
    if (safeIncludes(unit.title, mainTopic)) {
      score += 60;
    }
    
    // Matched direct query keywords
    if (matchedKeywords.length > 0) {
      const matchCount = matchedKeywords.filter(term => 
        unitTitle.includes(term) || 
        unitDesc.includes(term) ||
        unitTags.some(tag => tag.includes(term))
      ).length;
      
      if (matchCount > 0) {
        score += matchCount * 30; // Strong boost for mapped terms
      }
    }
    
    // Require at least one keyword match in title, description, or tags
    let hasKeywordMatch = false;
    
    // Check keyword matches in various fields
    for (const keyword of keywords) {
      // Title matches (high priority)
      if (safeIncludes(unit.title, keyword)) {
        score += 15;
        hasKeywordMatch = true;
      }
      
      // Description matches
      if (safeIncludes(unit.description, keyword)) {
        score += 10;
        hasKeywordMatch = true;
      }
      
      // Tag matches (high priority)
      if (unit.tags?.some(tag => safeIncludes(tag, keyword))) {
        score += 20;
        hasKeywordMatch = true;
      }
      
      // Tool name matches (for tool queries)
      if (unit.tools?.some(tool => safeIncludes(tool.name, keyword))) {
        score += 25;
        hasKeywordMatch = true;
      }
    }
    
    // If no keywords matched at all, return zero score
    if (!hasKeywordMatch && keywords.length > 0) {
      return { unit, score: 0 };
    }
    
    // Special handling for port scanning
    if (lowercaseQuery.includes('port scan') || lowercaseQuery.includes('scan port') || 
        lowercaseQuery.includes('open port') || lowercaseQuery.includes('network scan')) {
      if (unitTitle.includes('port scan') || 
          unitTitle.includes('network scan') || 
          unitTags.some(tag => 
            tag.includes('port scan') || 
            tag.includes('network scan') ||
            tag.includes('nmap')
          )) {
        score += 100; // Massive boost for port scanning related items
      }
    }
    
    return { unit, score };
  });
  
  // Filter units with a score > 0 and sort by score
  const relevantUnits = scoredUnits
    .filter(item => item.score > 0)
    .sort((a, b) => b.score - a.score)
    .map(item => item.unit);
  
  // Limit to reasonable number of results (max 15, or all if fewer)
  const maxResults = 15;
  const limitedResults = relevantUnits.slice(0, maxResults);
  
  console.log(`Found ${limitedResults.length} relevant units from ${relevantUnits.length} matches`);
  
  return limitedResults;
}

// initializeRAG function with better error handling
export async function initializeRAG(config) {
  try {
    // Check if already initialized
    if (store.initialized && store.knowledgeUnits.length > 0) {
      console.log('RAG already initialized with', store.knowledgeUnits.length, 'knowledge units');
      return true;
    }

    // Save API key if provided in config
    if (config && config.apiKey) {
      store.apiKey = config.apiKey;
      
      // Store in localStorage for persistence
      try {
        localStorage.setItem('rawpa_api_key', config.apiKey);
        console.log('API key saved to local storage');
      } catch (error) {
        console.warn('Could not store API key in localStorage:', error);
      }
    } else {
      // Try to get from localStorage if not provided
      try {
        const storedKey = localStorage.getItem('rawpa_api_key');
        if (storedKey) {
          store.apiKey = storedKey;
          console.log('API key loaded from local storage');
        }
      } catch (error) {
        console.warn('Could not retrieve API key from localStorage:', error);
      }
    }
    
    // Define paths to try for knowledge base file
    const pathsToTry = [
      '/ARD/ard1.json',
      '/public/ARD/ard1.json',
      '/aAyuiOcvMq/ARD/ard1.json',
      '../ARD/ard1.json',
      './ARD/ard1.json',
      './public/ARD/ard1.json',
      'ard1.json'
    ];

    // Load knowledge base with multiple retries
    const MAX_RETRIES = 3;
    let retries = 0;
    let success = false;
    
    while (retries < MAX_RETRIES && !success) {
      try {
        console.log(`Loading knowledge base (attempt ${retries + 1}/${MAX_RETRIES})...`);
        
        // Try each path until successful
        for (const path of pathsToTry) {
          try {
            console.log(`Trying path: ${path}`);
            const response = await fetch(path);
            
            if (response.ok) {
              const data = await response.json();
              if (Array.isArray(data) && data.length > 0) {
                store.knowledgeUnits = data;
                store.initialized = true;
                store.loadTimestamp = new Date();
                store.loadError = null;
                console.log(`Loaded ${store.knowledgeUnits.length} knowledge units from ${path} on ${store.loadTimestamp}`);
                return true; // Success
              } else {
                console.warn(`File found at ${path} but data is empty or invalid`);
              }
            } else {
              console.warn(`Failed to load from ${path}: ${response.status} ${response.statusText}`);
            }
          } catch (pathError) {
            console.warn(`Error loading from ${path}:`, pathError.message);
          }
        }
        
        // If we get here, none of the paths worked
        throw new Error(`Failed to load knowledge base from any known location`);
      } catch (error) {
        console.error(`Attempt ${retries + 1}/${MAX_RETRIES} failed:`, error);
        store.loadError = error.message;
        retries++;
        
        if (retries < MAX_RETRIES) {
          // Wait before retrying (exponential backoff)
          await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, retries)));
        }
      }
    }
    
    return false; // All attempts failed
  } catch (error) {
    console.error('Error initializing RAG:', error);
    store.loadError = error.message;
    return false;
  }
}

// getStoredAPIKey function
export function getStoredAPIKey() {
  if (store.apiKey) return store.apiKey;
  
  // Try to get from localStorage
  try {
    const storedKey = localStorage.getItem('rawpa_api_key');
    if (storedKey) {
      store.apiKey = storedKey;
      return storedKey;
    }
  } catch (error) {
    console.error('Failed to retrieve API key from localStorage:', error);
  }
  
  return null;
}

// storeAPIKey function
export function storeAPIKey(apiKey) {
  store.apiKey = apiKey;
  
  // Store in localStorage
  try {
    localStorage.setItem('rawpa_api_key', apiKey);
  } catch (error) {
    console.error('Failed to store API key in localStorage:', error);
  }
}

// Import the processRagQuery function from openaiService if available
let processRagQuery = null;
try {
  // Dynamic import to avoid circular dependencies
  import('./openaiService.ts').then(module => {
    processRagQuery = module.processRagQuery;
    console.log('Successfully imported openaiService for advanced RAG processing');
  }).catch(err => {
    console.warn('Could not import openaiService:', err);
  });
} catch (error) {
  console.warn('Error importing openaiService:', error);
}

// Improved sendRagQuery function
export async function sendRagQuery(query, queryType = QueryType.GENERAL) {
  if (!store.initialized) {
    // Try to initialize if not already done
    const success = await initializeRAG();
    if (!success) {
      return {
        answer: `Unable to initialize RAG system. Error: ${store.loadError || 'Unknown error'}. Please check your network connection and try again.`,
        relevantUnits: [],
        confidence: 0,
        sources: []
      };
    }
  }
  
  if (!store.apiKey) {
    return {
      answer: "API key not provided. Please set your HuggingFace API key in the settings to use the RAG system.",
      relevantUnits: [],
      confidence: 0,
      sources: []
    };
  }
  
  try {
    // Find relevant knowledge units
    const relevantUnits = searchKnowledgeUnits(query);
    
    if (relevantUnits.length === 0) {
      return {
        answer: "I couldn't find specific information related to your query in the knowledge base. Please try rephrasing your question or using more specific terminology. You can ask about topics like CSRF, XSS, SQL injection, or other common cybersecurity techniques.",
        relevantUnits: [],
        confidence: 0,
        sources: []
      };
    }
    
    // Check if we should use the advanced openaiService for this query
    // Import processRagQuery dynamically if not already imported
    if (!processRagQuery) {
      try {
        const module = await import('./openaiService.ts');
        processRagQuery = module.processRagQuery;
        console.log('Successfully imported openaiService for advanced RAG processing');
      } catch (err) {
        console.warn('Could not import openaiService:', err);
        // Don't attempt to use CommonJS require as it's not available in ES modules
      }
    }
    
    // Use openaiService for advanced processing if available
    if (processRagQuery) {
      console.log('Using advanced RAG processing with AI integration');
      return await processRagQuery(query, queryType);
    } else {
      console.warn('AI integration not available, falling back to basic response formatting');
    }
    
    // Format response based on query type
    let response = "";
    
    switch (queryType) {
      case QueryType.TECHNIQUE:
        response = formatTechniqueResponse(relevantUnits, query);
        break;
      case QueryType.PAYLOAD:
        response = formatPayloadResponse(relevantUnits, query);
        break;
      case QueryType.TOOL:
        response = formatToolResponse(relevantUnits, query);
        break;
      case QueryType.WAF_BYPASS:
        response = formatWafBypassResponse(relevantUnits, query);
        break;
      default:
        response = formatGeneralResponse(relevantUnits, query);
    }
    
    return {
      answer: response,
      relevantUnits: relevantUnits,
      confidence: 0.85,
      sources: relevantUnits.map(unit => ({
        title: unit.title,
        sku_id: unit.sku_id
      }))
    };
  } catch (error) {
    console.error('Error in RAG query:', error);
    
    return {
      answer: `I encountered an error while processing your query: ${error.message}. Please try again later.`,
      relevantUnits: [],
      confidence: 0,
      sources: []
    };
  }
}

// Response formatting functions based on query type
function formatGeneralResponse(units, query) {
  const mainUnit = units[0];
  
  // Safely get string value
  const safeString = (value) => {
    if (typeof value === 'string') return value;
    if (value === null || value === undefined) return '';
    return String(value);
  };
  
  return `
1. SUMMARY: ${safeString(mainUnit.title)} - ${safeString(mainUnit.description)}

2. DETAILED EXPLANATION:
   ${units.map((unit, i) => `- ${safeString(unit.title)}: ${safeString(unit.description)}`).join('\n   ')}
   
   ${mainUnit.techniques && Array.isArray(mainUnit.techniques) && mainUnit.techniques.length > 0 ? 
     `Common techniques include: ${mainUnit.techniques.map(t => `*${safeString(t)}*`).join(', ')}` : ''}
   
   ${mainUnit.tools && Array.isArray(mainUnit.tools) && mainUnit.tools.length > 0 ? 
     `Recommended tools: ${mainUnit.tools.map(t => `**${safeString(t.name)}**`).join(', ')}` : ''}

3. EXAMPLE(S):
   ${formatExamples(units)}
   
4. CONCLUSION:
   This information is sourced from the RAWPA security knowledge base. For more details, refer to the sources listed below.
  `;
}

function formatTechniqueResponse(units, query) {
  const mainUnit = units[0];
  
  // Safely get string value
  const safeString = (value) => {
    if (typeof value === 'string') return value;
    if (value === null || value === undefined) return '';
    return String(value);
  };
  
  return `
1. SUMMARY: ${safeString(mainUnit.title)} - Technical approach for ${safeString(mainUnit.target_component || 'security testing')}

2. DETAILED EXPLANATION:
   ${units.map((unit, i) => `- ${safeString(unit.title)}: ${safeString(unit.description)}`).join('\n   ')}
   
   ${mainUnit.techniques && Array.isArray(mainUnit.techniques) && mainUnit.techniques.length > 0 ? 
     `Key techniques include: ${mainUnit.techniques.map(t => `*${safeString(t)}*`).join(', ')}` : ''}
   
   ${mainUnit.severity ? `Severity: ${safeString(mainUnit.severity)}, Difficulty: ${safeString(mainUnit.difficulty || 'Moderate')}` : ''}

3. EXAMPLE(S):
   ${formatExamples(units)}
   
4. CONCLUSION:
   These techniques are part of the ${safeString(mainUnit.methodology || 'security assessment')} methodology and are typically used during the ${safeString(mainUnit.step || 'testing')} phase.
  `;
}

// Improved formatPayloadResponse function specifically for XSS
function formatPayloadResponse(units, query) {
  // Check if this is an XSS-related query
  const isXssQuery = query.toLowerCase().includes('xss');
  
  // Get the main unit
  const mainUnit = units[0];
  
  // Safely get string value
  const safeString = (value) => {
    if (typeof value === 'string') return value;
    if (value === null || value === undefined) return '';
    return String(value);
  };
  
  // Collect all payload examples
  const payloadExamples = units.flatMap(unit => 
    unit.payload_examples && Array.isArray(unit.payload_examples) ? 
    unit.payload_examples.map(payload => safeString(payload)).filter(p => p.trim() !== '') : 
    []
  );
  
  // Collect all code samples
  const codeSamples = units.flatMap(unit => 
    unit.code_samples && Array.isArray(unit.code_samples) ? 
    unit.code_samples.filter(sample => sample && typeof sample === 'object') : 
    []
  );
  
  // Format the response
  return `
1. SUMMARY: ${isXssQuery ? 'XSS Payload Examples' : `Payload examples for ${safeString(mainUnit.title)}`}

2. DETAILED EXPLANATION:
   ${units.map((unit, i) => `- ${safeString(unit.title)}: ${safeString(unit.description)}`).join('\n   ')}

3. EXAMPLE(S):
   ${payloadExamples.length > 0 ? 
     payloadExamples.map(payload => `\`\`\`\n${payload}\n\`\`\``).join('\n\n   ') : 
     'No specific payload examples available for this query.'}
   
   ${codeSamples.length > 0 ? 
     codeSamples.map(sample => `\`\`\`${safeString(sample.language)}\n${safeString(sample.code)}\n\`\`\``).join('\n\n   ') : 
     ''}
   
4. CONCLUSION:
   When testing for ${safeString(mainUnit.title)}, always ensure you have proper authorization and follow responsible disclosure practices.
  `;
}

function formatToolResponse(units, query) {
  // Safely get string value
  const safeString = (value) => {
    if (typeof value === 'string') return value;
    if (value === null || value === undefined) return '';
    return String(value);
  };
  
  return `
1. SUMMARY: Recommended tools for ${safeString(units[0].title)}

2. DETAILED EXPLANATION:
   ${units.map((unit, i) => `- ${safeString(unit.title)}: ${safeString(unit.description)}`).join('\n   ')}

3. RECOMMENDED TOOLS:
   ${units.flatMap(unit => 
     unit.tools && Array.isArray(unit.tools) ? 
     unit.tools.map(tool => `- **${safeString(tool.name)}**: ${safeString(tool.url || 'No URL provided')}`) : 
     []
   ).join('\n   ') || 'No specific tools found in the knowledge base for this query.'}
   
4. CONCLUSION:
   These tools can help you effectively test for and identify ${safeString(units[0].title.toLowerCase())} vulnerabilities. Always use security tools responsibly and legally.
  `;
}

function formatWafBypassResponse(units, query) {
  // Safely get string value
  const safeString = (value) => {
    if (typeof value === 'string') return value;
    if (value === null || value === undefined) return '';
    return String(value);
  };
  
  return `
1. SUMMARY: WAF bypass techniques for ${safeString(units[0].title)}

2. DETAILED EXPLANATION:
   ${units.map((unit, i) => `- ${safeString(unit.title)}: ${safeString(unit.description)}`).join('\n   ')}

3. BYPASS TECHNIQUES:
   ${units.flatMap(unit => 
     unit.waf_bypasses && Array.isArray(unit.waf_bypasses) && unit.waf_bypasses.length > 0 ? 
     unit.waf_bypasses.map(bypass => `- ${safeString(bypass)}`) : 
     []
   ).join('\n   ') || '- No specific WAF bypasses found in the knowledge base for this query.'}
   
4. CONCLUSION:
   When testing WAF bypasses, always ensure you have proper authorization and follow responsible disclosure practices.
  `;
}

function formatExamples(units) {
  // Safely get string value
  const safeString = (value) => {
    if (typeof value === 'string') return value;
    if (value === null || value === undefined) return '';
    return String(value);
  };
  
  // Get payload examples
  const payloads = units.flatMap(unit => 
    unit.payload_examples && Array.isArray(unit.payload_examples) ? 
    unit.payload_examples.map(payload => `\`\`\`\n${safeString(payload)}\n\`\`\``) : 
    []
  );
  
  // Get code samples
  const codeSamples = units.flatMap(unit => 
    unit.code_samples && Array.isArray(unit.code_samples) ? 
    unit.code_samples.filter(sample => sample && typeof sample === 'object')
      .map(sample => `\`\`\`${safeString(sample.language)}\n${safeString(sample.code)}\n\`\`\``) : 
    []
  );
  
  return [...payloads, ...codeSamples].join('\n\n   ') || 'No specific examples available in the knowledge base.';
}

// Export default object
export default {
  QueryType,
  getRAGStore,
  getAPIKey,
  searchKnowledgeUnits,
  initializeRAG,
  getStoredAPIKey,
  storeAPIKey,
  sendRagQuery
}; 